import { formatDate } from "../../common/constant/dates";
import { EmailStatus } from "../../common/requests/emails/endpoints";
import type { Tone } from "../../ui/StatusPill";

export const STATUS_META: Record<string, { label: string; tone: Tone; help: string }> = {
  sent: { label: "Sent", tone: "neutral", help: "Brevo accepted it; no delivery report yet." },
  deferred: { label: "Delayed", tone: "warning", help: "The receiving server asked Brevo to retry later." },
  delivered: { label: "Delivered", tone: "info", help: "Reached the inbox server. Not opened (or opens aren't tracked)." },
  opened: { label: "Opened", tone: "success", help: "Opened, or loaded by Apple Mail's privacy proxy." },
  clicked: { label: "Clicked", tone: "success", help: "A link in it was clicked." },
  unsubscribed: { label: "Unsubscribed", tone: "warning", help: "The recipient unsubscribed from it." },
  soft_bounce: { label: "Soft bounce", tone: "danger", help: "Temporarily refused (full inbox, server down)." },
  spam: { label: "Marked spam", tone: "danger", help: "The recipient reported it as spam." },
  blocked: { label: "Blocked", tone: "danger", help: "Brevo didn't send it: the address bounced or complained before." },
  invalid: { label: "Invalid", tone: "danger", help: "The address isn't valid." },
  hard_bounce: { label: "Hard bounce", tone: "danger", help: "Permanently refused: the address doesn't exist." },
  error: { label: "Error", tone: "danger", help: "Brevo failed to send it." },
};

/** Events in an email's timeline, in plain words. */
export const EVENT_LABEL: Record<string, string> = {
  requests: "Sent",
  deferred: "Delayed",
  delivered: "Delivered",
  opened: "Opened",
  uniqueOpened: "Opened",
  loadedByProxy: "Opened (Apple Mail proxy)",
  proxy_open: "Opened (Apple Mail proxy)",
  clicks: "Clicked",
  click: "Clicked",
  softBounces: "Soft bounce",
  hardBounces: "Hard bounce",
  blocked: "Blocked",
  invalid: "Invalid address",
  spam: "Marked spam",
  unsubscribed: "Unsubscribed",
  error: "Error",
};

export const statusMeta = (status: EmailStatus | string) =>
  STATUS_META[status] ?? { label: status, tone: "neutral" as Tone, help: "" };

const pad = (n: number) => String(n).padStart(2, "0");

/** "11 Oct 2026 13:21" in the browser's time zone */
export function formatWhen(iso: string | null | undefined) {
  if (!iso) return "–";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "–";
  return `${formatDate(d)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export const percent = (part: number, whole: number) => (whole ? `${Math.round((part / whole) * 100)}%` : "–");

/** YYYY-MM-DD for a local date */
export const isoDay = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export const daysAgo = (n: number, from = new Date()) => {
  const d = new Date(from);
  d.setDate(d.getDate() - n);
  return d;
};

export const errorMessage = (err: any, fallback: string) => err?.response?.data?.detail || fallback;

/** Brevo refuses calls from servers that aren't on its authorised IP list. */
export const isIpBlock = (message: string | null) => !!message && /unrecognised IP address/i.test(message);
